import * as React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { PageHeader } from '../../components/page-header/PageHeader';
import { BackButton } from '../../components/back-button/BackButton';
import { pageGutterSx } from '../../core/theme/layout';
import {
  DotykackaProduct,
  DotykackaProductMapping,
  DotykackaService,
} from '../../services/dotykacka/dotykackaService';
import { normalizeProductName } from '../../utils/normalizeProductName';

const dotykackaService = new DotykackaService();

const formatPrice = (price: number | null | undefined): string =>
  price == null ? '—' : Number(price).toFixed(2);

const pricesDiffer = (menuPrice: number | null, tillPrice: number | null | undefined): boolean =>
  menuPrice != null && tillPrice != null && Math.abs(Number(menuPrice) - Number(tillPrice)) > 0.005;

/** Maps each Menusso menu item to the Dotykačka till product it should appear as. */
export const DotykackaMappingPage: React.FC = () => {
  const { id: locationId } = useParams();
  const routerLocation = useLocation();
  const locationName = (routerLocation.state as { locationName?: string } | null)?.locationName;
  const { t } = useTranslation();
  const notify = useNotify();

  const [rows, setRows] = React.useState<DotykackaProductMapping[]>([]);
  const [products, setProducts] = React.useState<DotykackaProduct[]>([]);
  const [draft, setDraft] = React.useState<Record<number, string | null>>({});
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  // Refs, so a new notify/t identity (e.g. once i18n finishes loading) does not refetch from Dotypos.
  const notifyRef = React.useRef(notify);
  const tRef = React.useRef(t);
  notifyRef.current = notify;
  tRef.current = t;

  const productsById = React.useMemo(
    () => new Map(products.map((product) => [product.id, product])),
    [products],
  );

  const applyRows = (nextRows: DotykackaProductMapping[]) => {
    setRows(nextRows);
    setDraft(Object.fromEntries(nextRows.map((row) => [row.inventoryItemId, row.posId])));
  };

  React.useEffect(() => {
    if (!locationId) {
      return;
    }

    (async () => {
      try {
        const [mapping, tillProducts] = await Promise.all([
          dotykackaService.getProductMapping(locationId),
          dotykackaService.getProducts(locationId),
        ]);
        applyRows(mapping);
        setProducts(tillProducts);
      } catch (error: any) {
        Sentry.captureException(error);
        notifyRef.current(error?.response?.data?.error ?? tRef.current('dotykacka.errors.load'), { type: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [locationId]);

  const changedEntries = rows
    .filter((row) => (draft[row.inventoryItemId] ?? null) !== (row.posId ?? null))
    .map((row) => ({ inventoryItemId: row.inventoryItemId, posId: draft[row.inventoryItemId] ?? null }));

  const unmappedCount = rows.filter((row) => !draft[row.inventoryItemId]).length;

  const handleSuggest = () => {
    const productsByName = new Map<string, DotykackaProduct[]>();
    products.forEach((product) => {
      const key = normalizeProductName(product.name);
      productsByName.set(key, [...(productsByName.get(key) ?? []), product]);
    });

    let suggested = 0;
    const next = { ...draft };

    rows.forEach((row) => {
      const matches = productsByName.get(normalizeProductName(row.name)) ?? [];

      // Only unambiguous matches: two till products with the same name need a human.
      if (!next[row.inventoryItemId] && matches.length === 1) {
        next[row.inventoryItemId] = matches[0].id;
        suggested++;
      }
    });

    setDraft(next);
    notify(
      suggested > 0 ? t('dotykacka.mapping.suggested', { count: suggested }) : t('dotykacka.mapping.suggestedNone'),
      { type: suggested > 0 ? 'info' : 'warning' },
    );
  };

  const handleSave = async () => {
    if (!locationId) {
      return;
    }

    setSaving(true);
    try {
      applyRows(await dotykackaService.saveProductMapping(locationId, changedEntries));
      notify(t('dotykacka.mapping.saved'), { type: 'success' });
    } catch (error: any) {
      Sentry.captureException(error);
      notify(error?.response?.data?.error ?? t('dotykacka.errors.saveMapping'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ pb: 'var(--space-8)' }}>
      <PageHeader
        back={<BackButton />}
        title={t('dotykacka.mapping.title')}
        subtitle={locationName}
        actions={(
          <Stack direction="row" spacing="var(--space-2)">
            <Button variant="outlined" onClick={handleSuggest} disabled={loading || saving} sx={{ textTransform: 'none' }}>
              {t('dotykacka.mapping.suggest')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || saving || changedEntries.length === 0}
              sx={{
                textTransform: 'none',
                boxShadow: 'none',
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-text-inverse)',
                '&:hover': { backgroundColor: 'var(--color-primary-hover)' },
              }}
            >
              {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('dotykacka.mapping.save', { count: changedEntries.length })}
            </Button>
          </Stack>
        )}
      />

      <Box sx={pageGutterSx}>
        {loading && <CircularProgress />}

        {!loading && (
          <>
            <Alert severity={unmappedCount > 0 ? 'warning' : 'success'} sx={{ mb: 'var(--space-4)' }}>
              {unmappedCount > 0
                ? t('dotykacka.mapping.unmappedHint', { count: unmappedCount })
                : t('dotykacka.mapping.allMapped')}
            </Alert>

            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('dotykacka.mapping.menuItem')}</TableCell>
                    <TableCell align="right">{t('dotykacka.mapping.menuPrice')}</TableCell>
                    <TableCell sx={{ minWidth: 320 }}>{t('dotykacka.mapping.tillProduct')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const posId = draft[row.inventoryItemId] ?? null;
                    const product = posId ? productsById.get(posId) ?? null : null;
                    const missingOnTill = posId != null && product == null;

                    return (
                      <TableRow key={row.inventoryItemId}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell align="right">{formatPrice(row.price)}</TableCell>
                        <TableCell>
                          <Autocomplete
                            size="small"
                            options={products}
                            value={product}
                            onChange={(_, selected) => setDraft({ ...draft, [row.inventoryItemId]: selected?.id ?? null })}
                            getOptionLabel={(option) => `${option.name} · ${formatPrice(option.priceWithVat)}`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                              <TextField {...params} placeholder={t('dotykacka.mapping.notMapped')} />
                            )}
                          />
                          {missingOnTill && (
                            <Typography sx={{ mt: 'var(--space-1)', fontSize: 'var(--text-xs)', color: 'var(--color-error)' }}>
                              {t('dotykacka.mapping.missingOnTill')}
                            </Typography>
                          )}
                          {product && pricesDiffer(row.price, product.priceWithVat) && (
                            <Typography sx={{ mt: 'var(--space-1)', fontSize: 'var(--text-xs)', color: 'var(--color-warning)' }}>
                              {t('dotykacka.mapping.priceDiffers', { price: formatPrice(product.priceWithVat) })}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default DotykackaMappingPage;
